import './util/handleError';
import './global/init';
import './global/actions/initial';

import React from './lib/teact/teact';
import TeactDOM from './lib/teact/teact-dom';
import {
  getActions, getGlobal,
} from './global';

import {
  DEBUG, IS_PWA_ENABLED, SSG_DEFAULT_LANG_CODE, STRICTERDOM_ENABLED,
} from './config';
import { enableStrict, requestMutation } from './lib/fasterdom/fasterdom';
import { selectSharedSettings } from './global/selectors/sharedState';
import { selectTabState } from './global/selectors/tabs';
import { initAttrReport } from './util/attrReport';
import {
  clearMultitabPresence, requestGlobal, startMultitabPresence, subscribeToMultitabBroadcastChannel,
} from './util/browser/multitab';
import { initShellBridge } from './util/browser/shellContainer';
import { initClientDiagnostics, recordClientDiagnosticEvent } from './util/clientDiagnostics';
import { establishMultitabRole, subscribeToMasterChange } from './util/establishMultitabRole';
import { initGlobal } from './util/init';
import { initLocalization } from './util/localization';
import { COMMON_UI_TEXT, pickCustomUiText } from './util/localization/customUiTexts';
import { Bundles, loadBundle } from './util/moduleLoader';
import { initChannelCode } from './util/nativeBridge';
import { initSafeArea } from './util/safeArea';
import { onBeforeUnload } from './util/schedulers';
import { initShellChatState } from './util/shellChatState';
import { initShellOpenChat } from './util/shellOpenChat';
import { initShellWatchdog } from './util/shellWatchdog';
import { markStartupPerf, printStartupPerfSummary, reportStartupPhases } from './util/startupPerf';
import { warmUpApiWorker } from './api/gramjs/worker/connector';

import App from './components/App';

import './assets/fonts/roboto.css';
import './styles/index.scss';

const STARTUP_PHASE_FALLBACK_MS = 15000;
const SPLASH_REMOVE_FALLBACK_MS = 300;

if (STRICTERDOM_ENABLED) {
  enableStrict();
}

window.setTimeout(() => {
  markStartupPerf('init_scheduled');
  void init();
}, 0);

function removeSplashScreen() {
  let isRemoved = false;

  const run = (reason: string) => {
    if (isRemoved) return;
    isRemoved = true;
    markStartupPerf('splash_remove_start', { reason });
    document.getElementById('splash')?.remove();
    markStartupPerf('splash_removed');
    printStartupPerfSummary();
  };

  window.requestAnimationFrame(() => run('raf'));
  window.setTimeout(() => run('timeout'), SPLASH_REMOVE_FALLBACK_MS);
}

function runPostMountTasks() {
  window.requestAnimationFrame(() => {
    window.setTimeout(() => {
      markStartupPerf('post_mount_tasks_start');
      if (getGlobal().authState !== 'authorizationStateReady') {
        markStartupPerf('auth_bundle_prefetch_scheduled');
        void loadBundle(Bundles.Auth);
      }

      if (IS_PWA_ENABLED) {
        void import('./util/updateWebmanifest').then(({ default: updateWebmanifest }) => {
          markStartupPerf('update_webmanifest_loaded');
          updateWebmanifest();
        });
      }
      void import('./util/betterView').then(({ betterView }) => {
        markStartupPerf('better_view_loaded');
        betterView();
      });
      void import('./util/setupServiceWorker').then(({ default: setupServiceWorker }) => {
        markStartupPerf('service_worker_setup_loaded');
        setupServiceWorker();
      });
    }, 0);
  });
}

async function init() {
  markStartupPerf('init_start');

  if (DEBUG) {
    // eslint-disable-next-line no-console
    console.log('>>> INIT');
  }

  if (!(window as any).isCompatTestPassed) return;

  initChannelCode();
  initAttrReport();
  initSafeArea();

  await window.electron?.restoreLocalStorage();

  subscribeToMultitabBroadcastChannel();
  warmUpApiWorker();

  markStartupPerf('request_global_start');
  await requestGlobal(APP_VERSION);
  markStartupPerf('request_global_done');
  startMultitabPresence();
  onBeforeUnload(() => {
    const global = getGlobal();
    if (Object.keys(global.byTabId).length === 1) {
      clearMultitabPresence();
    }
  });

  initShellBridge();
  initClientDiagnostics();

  markStartupPerf('init_global_start');
  await initGlobal();
  markStartupPerf('init_global_done');
  getActions().init();
  markStartupPerf('actions_init_done');
  initShellOpenChat();
  initShellWatchdog();
  initShellChatState();

  getActions().updateShouldEnableDebugLog();
  getActions().updateShouldDebugExportedSenders();

  const global = getGlobal();

  const sharedSettings = selectSharedSettings(global);
  const initialLanguage = sharedSettings.language || SSG_DEFAULT_LANG_CODE;

  const splashText = document.querySelector('#splash .splash-text');
  if (splashText) {
    splashText.textContent = pickCustomUiText(COMMON_UI_TEXT.appLoading, { rawCode: initialLanguage });
  }

  initLocalization(initialLanguage, true);

  subscribeToMasterChange((isMasterTab) => {
    recordClientDiagnosticEvent({
      type: 'multitab_role',
      reason: `master:${isMasterTab ? 1 : 0}`,
      success: true,
    }, { upload: true });

    getActions()
      .switchMultitabRole({ isMasterTab }, { forceSyncOnIOs: true });
  });
  const shouldReestablishMasterToSelf = getGlobal().authState !== 'authorizationStateReady';
  establishMultitabRole(shouldReestablishMasterToSelf);

  if (DEBUG) {
    // eslint-disable-next-line no-console
    console.log('>>> START INITIAL RENDER');
  }

  requestMutation(() => {
    markStartupPerf('react_render_start');
    TeactDOM.render(
      <App />,
      document.getElementById('root')!,
    );
    markStartupPerf('react_render_done');

    removeSplashScreen();
  });

  runPostMountTasks();
  markStartupPerf('post_mount_tasks_scheduled');
  window.setTimeout(reportStartupPhases, STARTUP_PHASE_FALLBACK_MS);

  if (DEBUG) {
    // eslint-disable-next-line no-console
    console.log('>>> FINISH INITIAL RENDER');
  }

  if (DEBUG) {
    document.addEventListener('dblclick', () => {
      // eslint-disable-next-line no-console
      console.warn('TAB STATE', selectTabState(getGlobal()));
      // eslint-disable-next-line no-console
      console.warn('GLOBAL STATE', getGlobal());
    });
  }
}
